import { t } from '@/features/i18n/utils';
import { Button } from '@/ui/components/button';
import { useAuth } from '@/features/auth';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Icon } from '@/ui/components/icon';
import { PlusSignIcon } from '@hugeicons-pro/core-stroke-rounded';
import { Empty, EmptyContent, EmptyDescription, EmptyTitle } from '@/ui/components/empty';
import { useSwitchOrganization } from '@/features/user/mutations';
import { Badge } from '@/ui/components/badge';
import { OrganizationAvatar } from '@/features/organizations/components/organization-avatar';
import { Logo } from '@/ui/components/logo';

export const Route = createFileRoute('/_authed/choose-organization')({
  component: RouteComponent,
});

function RouteComponent() {
  const auth = useAuth();
  const switchOrganization = useSwitchOrganization();

  if (auth.user.organizations.length === 0) {
    return (
      <Empty>
        <EmptyContent>
          <Logo className="mb-3" />
          <EmptyTitle>
            {t({ en: 'Add an organisation', nb: 'Legg til en organisasjon' })}
          </EmptyTitle>
          <EmptyDescription>
            {t({
              en: "Create an organisation to get started. We'll review it before you can use the app.",
              nb: 'Opprett en organisasjon for å komme i gang. Vi vurderer den før du kan bruke appen.',
            })}
          </EmptyDescription>
          <div className="flex gap-1.5">
            <Button nativeButton={false} render={<Link to="/add-organization" />}>
              <Icon icon={PlusSignIcon} />
              {t({ en: 'Create an organisation...', nb: 'Opprett organisasjon...' })}
            </Button>
            <Button variant="secondary" onClick={() => auth.logout()}>
              {t({ en: 'Logout', nb: 'Logg ut' })}
            </Button>
          </div>
        </EmptyContent>
      </Empty>
    );
  }
  return (
    <Empty className="w-full">
      <EmptyContent>
        <Logo className="mb-3" />
        <EmptyTitle>{t({ en: 'Select organisation', nb: 'Velg organisasjon' })}</EmptyTitle>
        {auth.user.organizations.some((org) => !org.isEnabled) ? (
          <EmptyDescription>
            {t({
              en: "Pending organisations can't be opened until approved.",
              nb: 'Organisasjoner som venter kan ikke åpnes før de er godkjent.',
            })}
          </EmptyDescription>
        ) : null}
        <div className="mt-4 flex flex-col gap-1 w-full">
          {auth.user.organizations.map((org) => (
            <Button
              key={org.organizationId}
              onClick={() => switchOrganization.mutate(org.organizationId)}
              disabled={!org.isEnabled || switchOrganization.isPending}
              variant="secondary"
              size="lg"
              className="justify-between w-full px-1.5"
            >
              <span className="flex items-center gap-2 min-w-0">
                <OrganizationAvatar
                  organization={{ id: org.organizationId, name: org.organizationName }}
                  size={24}
                  className="rounded-[calc(var(--radius-md)-0.125rem)]"
                />
                <span className="text-sm truncate">{org.organizationName}</span>
              </span>
              {!org.isEnabled ? (
                <Badge size="xs" variant="tertiary-warning">
                  {t({ en: 'Pending approval', nb: 'Venter på godkjenning' })}
                </Badge>
              ) : org.organizationId === auth.user.organizationId ? (
                <Badge size="xs" variant="tertiary-accent">
                  {t({ en: 'Current', nb: 'Gjeldende' })}
                </Badge>
              ) : null}
            </Button>
          ))}
        </div>
        <Button
          nativeButton={false}
          render={<Link to="/add-organization" />}
          size="lg"
          className="w-full mt-4"
        >
          <Icon icon={PlusSignIcon} />
          {t({ en: 'Create an organisation...', nb: 'Opprett organisasjon...' })}
        </Button>
      </EmptyContent>
    </Empty>
  );
}
